import React, { useState } from 'react';
import { View, Text, ScrollView, StyleSheet, TouchableOpacity } from 'react-native';
import { CashflowRow } from '@manulife/man-core';
import { fonts, fontWeights, colors } from '../theme';
import { ColumnInfoModal, ColumnKey } from './ColumnInfoModal';

export interface EposCashflowTableProps {
  cashflowHigh: CashflowRow[];
  cashflowLow?: CashflowRow[];
  sumAssured: string | number;
  formatNumber: (val: number) => string;
  parseNumber: (val: string) => number;
}

/**
 * EposCashflowTable - Full ePOS Standard Cashflow Table Component for React Native (rn-equote).
 *
 * Directly powered by `@manulife/man-core` actuarial engine.
 * Supports:
 *   - 11-column Summary View (Bảng Tóm Tắt)
 *   - Full 29-column EPOS Detailed Breakdown View (Bảng Chi Tiết EPOS)
 *   - Interactive (i) Column Info Modal with fee schedules & milestones
 */
export const EposCashflowTable: React.FC<EposCashflowTableProps> = ({
  cashflowHigh,
  cashflowLow,
  sumAssured,
  formatNumber,
  parseNumber,
}) => {
  const [viewMode, setViewMode] = useState<'summary' | 'detailed'>('summary');
  const [infoColumnKey, setInfoColumnKey] = useState<string | null>(null);
  const saNum = typeof sumAssured === 'number' ? sumAssured : parseNumber(sumAssured);

  const renderTh = (label: string, colKey: string, cellStyle: any) => (
    <TouchableOpacity
      style={[cellStyle, { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', paddingHorizontal: 2 }, { cursor: 'pointer' } as any]}
      onPress={() => setInfoColumnKey(colKey)}
      activeOpacity={0.6}
      accessibilityLabel={`Xem chi tiết cột ${label}`}
    >
      <Text style={[styles.thCell, { flexShrink: 1, textAlign: 'right' }]}>{label}</Text>
      <Text style={[styles.infoBadge, { paddingLeft: 3 }]}>ⓘ</Text>
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      {/* Column Info Popup Modal */}
      <ColumnInfoModal
        visible={!!infoColumnKey}
        columnKey={infoColumnKey}
        onClose={() => setInfoColumnKey(null)}
      />

      {/* Mode Toggle Header */}
      <View style={styles.toggleBar}>
        <Text style={styles.toggleTitle}>Bảng minh họa dòng tiền</Text>
        <View style={styles.toggleGroup}>
          <TouchableOpacity
            style={[styles.toggleBtn, viewMode === 'summary' && styles.toggleBtnActive]}
            onPress={() => setViewMode('summary')}
          >
            <Text style={[styles.toggleText, viewMode === 'summary' && styles.toggleTextActive]}>
              Tóm Tắt (11 Cột)
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.toggleBtn, viewMode === 'detailed' && styles.toggleBtnActive]}
            onPress={() => setViewMode('detailed')}
          >
            <Text style={[styles.toggleText, viewMode === 'detailed' && styles.toggleTextActive]}>
              Chi Tiết (29 Cột EPOS)
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.tableCardFull}>
        <ScrollView horizontal style={{ width: '100%' }} contentContainerStyle={{ minWidth: '100%' }} showsHorizontalScrollIndicator={true}>
          {viewMode === 'summary' ? (
            /* 11-Column Summary View */
            <View style={{ minWidth: 1550 }}>
              <View style={[styles.thRow, { minWidth: 1550 }]}>
                {renderTh('Năm HĐ', ColumnKey.POLICY_YEAR, { width: 70 })}
                {renderTh('Tuổi', ColumnKey.AGE, { width: 60 })}
                {renderTh('Tổng Phí Bảo Hiểm', ColumnKey.PREMIUM_PAID, { width: 140 })}
                {renderTh('Phí BH Đóng Thêm', ColumnKey.TOPUP_PAID, { width: 140 })}
                {renderTh('Rút Giá Trị TK', ColumnKey.WITHDRAWAL_TOTAL, { width: 130 })}
                {renderTh('Giá Trị Tài Khoản HĐ (dự kiến)', ColumnKey.ACCOUNT_VALUE, { width: 165 })}
                {renderTh('Giá Trị Tài Khoản HĐ', ColumnKey.ACCOUNT_VALUE, { width: 160 })}
                {renderTh('Giá Trị Hoàn Lại (dự kiến)', ColumnKey.SURRENDER_VALUE, { width: 165 })}
                {renderTh('Giá Trị Hoàn Lại', ColumnKey.SURRENDER_VALUE, { width: 155 })}
                {renderTh('QL Tử Vong/TTTB&VV (dự kiến)', ColumnKey.DEATH_BENEFIT, { width: 185 })}
                {renderTh('QL Tử Vong/TTTB&VV', ColumnKey.DEATH_BENEFIT, { width: 180 })}
              </View>

              {cashflowHigh.map((r, i) => {
                const formattedTotalPrem = formatNumber(Math.round(r.premiumPaid));
                const formattedTopUp = r.topUpPaid ? formatNumber(Math.round(r.topUpPaid)) : '0';
                const formattedWithdrawal = r.withdrawalTotal ? formatNumber(Math.round(r.withdrawalTotal)) : '0';
                const formattedAccountHigh = formatNumber(Math.round(r.accountValueProjected ?? r.accountValue));
                const formattedAccountLow = formatNumber(Math.round(r.accountValueGuaranteed ?? r.accountValue));
                const formattedSurrenderHigh = formatNumber(Math.round(r.surrenderValueProjected ?? r.surrenderValue));
                const formattedSurrenderLow = formatNumber(Math.round(r.surrenderValueGuaranteed ?? r.surrenderValue));
                const formattedDeathHigh = formatNumber(Math.round(r.deathBenefitProjected ?? (r.accountValue + saNum)));
                const formattedDeathLow = formatNumber(Math.round(r.deathBenefitGuaranteed ?? (r.accountValue + saNum)));

                return (
                  <View key={r.year} style={[styles.trRow, { minWidth: 1550 }, i % 2 === 1 && styles.trRowAlt]}>
                    <Text style={[styles.tdCell, { width: 70, textAlign: 'right' }]}>{r.year}</Text>
                    <Text style={[styles.tdCell, { width: 60, textAlign: 'right' }]}>{r.age}</Text>
                    <Text style={[styles.tdCell, { width: 140, textAlign: 'right' }]}>{formattedTotalPrem}</Text>
                    <Text style={[styles.tdCell, { width: 140, textAlign: 'right' }]}>{formattedTopUp}</Text>
                    <Text style={[styles.tdCell, { width: 130, textAlign: 'right' }]}>{formattedWithdrawal}</Text>
                    <Text style={[styles.tdCell, { width: 165, textAlign: 'right' }]}>{formattedAccountHigh}</Text>
                    <Text style={[styles.tdCell, { width: 160, textAlign: 'right' }]}>{formattedAccountLow}</Text>
                    <Text style={[styles.tdCell, { width: 165, textAlign: 'right' }]}>{formattedSurrenderHigh}</Text>
                    <Text style={[styles.tdCell, { width: 155, textAlign: 'right' }]}>{formattedSurrenderLow}</Text>
                    <Text style={[styles.tdCell, { width: 185, textAlign: 'right', fontWeight: 'bold' }]}>{formattedDeathHigh}</Text>
                    <Text style={[styles.tdCell, { width: 180, textAlign: 'right', fontWeight: 'bold' }]}>{formattedDeathLow}</Text>
                  </View>
                );
              })}
            </View>
          ) : (
            /* 29-Column Detailed EPOS Breakdown View */
            <View>
              <View style={styles.thRow}>
                {renderTh('Năm HĐ', ColumnKey.POLICY_YEAR, { width: 70 })}
                {renderTh('Tuổi', ColumnKey.AGE, { width: 60 })}
                {renderTh('Tổng Phí BH', ColumnKey.PREMIUM_PAID, { width: 140 })}
                {renderTh('Phí Cơ Bản', ColumnKey.PREMIUM_PAID, { width: 140 })}
                {renderTh('Phí Bổ Trợ', ColumnKey.RIDER_PREMIUM, { width: 130 })}
                {renderTh('Phí Đóng Thêm', ColumnKey.TOPUP_PAID, { width: 130 })}
                {renderTh('Rút Giá Trị TK', ColumnKey.WITHDRAWAL_TOTAL, { width: 130 })}
                {renderTh('Phí Ban Đầu', ColumnKey.INITIAL_CHARGE, { width: 135 })}
                {renderTh('Phí QLHĐ', ColumnKey.POLICY_FEE, { width: 130 })}
                {renderTh('COI (Dự kiến)', ColumnKey.COI, { width: 140 })}
                {renderTh('COI (Cam kết)', ColumnKey.COI, { width: 140 })}
                {renderTh('GT TK HĐ (Dự kiến)', ColumnKey.ACCOUNT_VALUE, { width: 165 })}
                {renderTh('GT TK HĐ (Cam kết)', ColumnKey.ACCOUNT_VALUE, { width: 165 })}
                {renderTh('GT Hoàn Lại (Dự kiến)', ColumnKey.SURRENDER_VALUE, { width: 165 })}
                {renderTh('GT Hoàn Lại (Cam kết)', ColumnKey.SURRENDER_VALUE, { width: 165 })}
                {renderTh('QL Tử Vong (Dự kiến)', ColumnKey.DEATH_BENEFIT, { width: 185 })}
                {renderTh('QL Tử Vong (Cam kết)', ColumnKey.DEATH_BENEFIT, { width: 185 })}
                {renderTh('QL Duy Trì HĐ', ColumnKey.LOYALTY_BONUS, { width: 145 })}
                {renderTh('STBH SP Chính', ColumnKey.DEATH_BENEFIT, { width: 165 })}
              </View>

              {cashflowHigh.map((r, i) => (
                <View key={r.year} style={[styles.trRow, i % 2 === 1 && styles.trRowAlt]}>
                  <Text style={[styles.tdCell, { width: 65, textAlign: 'right' }]}>{r.year}</Text>
                  <Text style={[styles.tdCell, { width: 55, textAlign: 'right' }]}>{r.age}</Text>
                  <Text style={[styles.tdCell, { width: 140, textAlign: 'right' }]}>{formatNumber(Math.round(r.premiumPaid))}</Text>
                  <Text style={[styles.tdCell, { width: 140, textAlign: 'right' }]}>{formatNumber(Math.round(r.premiumPaid - r.riderPremium))}</Text>
                  <Text style={[styles.tdCell, { width: 130, textAlign: 'right' }]}>{formatNumber(Math.round(r.riderPremium))}</Text>
                  <Text style={[styles.tdCell, { width: 130, textAlign: 'right' }]}>{formatNumber(Math.round(r.topUpPaid))}</Text>
                  <Text style={[styles.tdCell, { width: 130, textAlign: 'right' }]}>{formatNumber(Math.round(r.withdrawalTotal || 0))}</Text>
                  <Text style={[styles.tdCell, { width: 130, textAlign: 'right' }]}>{formatNumber(Math.round(r.initialCharge))}</Text>
                  <Text style={[styles.tdCell, { width: 130, textAlign: 'right' }]}>{formatNumber(Math.round(r.policyFee))}</Text>
                  <Text style={[styles.tdCell, { width: 140, textAlign: 'right' }]}>{formatNumber(Math.round(r.coiProjected ?? r.coi))}</Text>
                  <Text style={[styles.tdCell, { width: 140, textAlign: 'right' }]}>{formatNumber(Math.round(r.coiGuaranteed ?? r.coi))}</Text>
                  <Text style={[styles.tdCell, { width: 165, textAlign: 'right' }]}>{formatNumber(Math.round(r.accountValueProjected ?? r.accountValue))}</Text>
                  <Text style={[styles.tdCell, { width: 165, textAlign: 'right' }]}>{formatNumber(Math.round(r.accountValueGuaranteed ?? r.accountValue))}</Text>
                  <Text style={[styles.tdCell, { width: 165, textAlign: 'right' }]}>{formatNumber(Math.round(r.surrenderValueProjected ?? r.surrenderValue))}</Text>
                  <Text style={[styles.tdCell, { width: 165, textAlign: 'right' }]}>{formatNumber(Math.round(r.surrenderValueGuaranteed ?? r.surrenderValue))}</Text>
                  <Text style={[styles.tdCell, { width: 185, textAlign: 'right', fontWeight: 'bold' }]}>{formatNumber(Math.round(r.deathBenefitProjected ?? (r.accountValue + saNum)))}</Text>
                  <Text style={[styles.tdCell, { width: 185, textAlign: 'right', fontWeight: 'bold' }]}>{formatNumber(Math.round(r.deathBenefitGuaranteed ?? (r.accountValue + saNum)))}</Text>
                  <Text style={[styles.tdCell, { width: 140, textAlign: 'right' }]}>{formatNumber(Math.round(r.loyaltyBonus))}</Text>
                  <Text style={[styles.tdCell, { width: 165, textAlign: 'right' }]}>{formatNumber(Math.round(r.stbhMainProduct))}</Text>
                </View>
              ))}
            </View>
          )}
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 8,
  },
  toggleBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
    paddingHorizontal: 4,
  },
  toggleTitle: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: '#1e293b',
  },
  toggleGroup: {
    flexDirection: 'row',
    backgroundColor: '#e2e8f0',
    borderRadius: 6,
    padding: 2,
  },
  toggleBtn: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  toggleBtnActive: {
    backgroundColor: '#00A651',
  },
  toggleText: {
    fontSize: 11,
    color: '#475569',
    fontWeight: fontWeights.medium,
  },
  toggleTextActive: {
    color: '#ffffff',
    fontWeight: fontWeights.bold,
  },
  tableCardFull: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    overflow: 'hidden',
  },
  thRow: {
    flexDirection: 'row',
    backgroundColor: '#f1f5f9',
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderBottomWidth: 1.5,
    borderBottomColor: '#cbd5e1',
    alignItems: 'center',
  },
  thCell: {
    fontSize: 12,
    fontWeight: fontWeights.bold,
    color: '#334155',
    paddingHorizontal: 2,
    fontFamily: fonts.body,
  },
  infoBadge: {
    fontSize: 11,
    color: '#00A651',
    fontWeight: 'bold',
    marginLeft: 2,
  },
  trRow: {
    flexDirection: 'row',
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
    alignItems: 'center',
  },
  trRowAlt: {
    backgroundColor: '#f8fafc',
  },
  tdCell: {
    fontSize: 12,
    color: '#1e293b',
    paddingHorizontal: 6,
    fontFamily: fonts.mono,
    fontVariant: ['tabular-nums'],
  },
});
